import { useState } from "react";
import { motion } from "framer-motion";
import { Plane, ArrowRight, Clock } from "lucide-react";
import { Badge } from "./ui/badge";

const statusStyles = {
  Confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Waitlisted: "bg-amber-100 text-amber-800 border-amber-200",
  Pending: "bg-amber-100 text-amber-800 border-amber-200",
  Cancelled: "bg-red-100 text-red-700 border-red-200",
};

function AirlineLogo({ code, url, name }) {
  const [broken, setBroken] = useState(false);
  if (url && !broken) {
    return (
      <img
        src={url}
        alt={name}
        onError={() => setBroken(true)}
        data-testid="segment-airline-logo"
        className="h-9 w-9 rounded-md object-contain border border-slate-200 bg-white p-0.5"
      />
    );
  }
  return (
    <div
      data-testid="segment-airline-logo-fallback"
      className="h-9 w-9 rounded-md border border-slate-200 bg-slate-900 text-white flex items-center justify-center font-pnr text-sm font-medium"
    >
      {code}
    </div>
  );
}

function Endpoint({ label, time, code, name, city, align, note }) {
  return (
    <div className={align === "right" ? "text-right" : "text-left"}>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">{label}</div>
      <div className="flex items-baseline gap-2 mt-0.5" style={{ justifyContent: align === "right" ? "flex-end" : "flex-start" }}>
        <span className="font-heading text-xl font-bold text-slate-900 leading-none">{time}</span>
        <span className="font-pnr text-sm text-blue-600 font-medium">{code}</span>
        {note ? <span className="text-[11px] font-bold text-red-500">{note}</span> : null}
      </div>
      <div className="text-sm text-slate-600 mt-1 leading-snug">{name}</div>
      {city ? <div className="text-xs text-slate-400">{city}</div> : null}
    </div>
  );
}

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

function dayOffset(dep, arr) {
  const parse = (s) => {
    const m = /^(\d{1,2})\s+([A-Za-z]{3})(?:\s+(\d{4}))?$/.exec((s || "").trim());
    if (!m) return null;
    const mon = MONTHS.indexOf(m[2].toLowerCase());
    return mon < 0 ? null : Date.UTC(m[3] ? Number(m[3]) : 2001, mon, Number(m[1]));
  };
  const a = parse(dep), b = parse(arr);
  if (a == null || b == null) return 0;
  let diff = Math.round((b - a) / 86400000);
  if (diff < -300) diff += 365;
  return diff;
}

export function SegmentCard({ segment, index }) {
  const s = segment;
  const badgeClass = statusStyles[s.status] || "bg-slate-100 text-slate-700 border-slate-200";
  const offset = dayOffset(s.date, s.arrival_date);
  const overnight = offset > 0;
  return (
    <motion.div
      data-testid={`segment-${index}`}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.06, ease: "easeOut" }}
      className="rounded-lg border border-slate-200 bg-white p-3.5 hover:shadow-sm transition-shadow duration-200"
    >
      <div className="flex items-center justify-between gap-3 pb-3 border-b border-slate-100">
        <div className="flex items-center gap-2.5 min-w-0">
          <AirlineLogo code={s.airline_code} url={s.airline_logo_url} name={s.airline_name} />
          <div className="min-w-0">
            <div data-testid="segment-airline-name" className="font-heading font-bold text-sm text-slate-900 truncate">
              {s.airline_name}
            </div>
            <div className="text-xs text-slate-500 font-pnr">
              {s.airline_code}{s.flight_number} &middot; {s.date}
            </div>
          </div>
        </div>
        <Badge
          data-testid="segment-status"
          variant="outline"
          className={`rounded-full text-xs font-medium ${badgeClass}`}
        >
          {s.status}
        </Badge>
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 py-3">
        <Endpoint label="Departure" time={s.departure_time} code={s.origin_code} name={s.origin_name} city={s.origin_city} align="left" />
        <div className="flex flex-col items-center text-slate-300">
          <Plane className="h-4 w-4 rotate-90 text-blue-500" />
          <div className="w-16 border-t border-dashed border-slate-300 my-1" />
          {s.duration
            ? <span data-testid="segment-duration" className="text-[11px] font-semibold text-slate-500 whitespace-nowrap">{s.duration}</span>
            : <ArrowRight className="h-4 w-4 text-slate-400" />}
        </div>
        <Endpoint label="Arrival" time={s.arrival_time} code={s.destination_code} name={s.destination_name} city={s.destination_city} align="right" note={overnight ? `+${offset} day${offset > 1 ? "s" : ""}` : null} />
      </div>

      <div className="flex items-center gap-4 pt-2.5 border-t border-slate-100 text-sm">
        <span className="inline-flex items-center gap-1.5 text-slate-600">
          <Clock className="h-3.5 w-3.5 text-slate-400" />
          {s.cabin_class}
          <span className="font-pnr text-slate-400">({s.booking_class})</span>
        </span>
        {overnight ? (
          <span data-testid="segment-arrival-date" className="text-slate-500">Arrives {s.arrival_date}</span>
        ) : null}
      </div>
    </motion.div>
  );
}
